/**
 * IGGG Primitives — workspace/index.html 시각 톤 React 컴포넌트.
 *
 * - IGGGShell:        그레인 오버레이 + 모노톤 배경
 * - IGGGBrand:        좌상단 brand mark (ESTIMATE / sub)
 * - IGGGSectionHeader 넘버 + 한글타이틀 + mono 서브 라벨
 * - IGGGCard:         bg-elev + line-soft border (4px radius)
 * - IGGGRow:          데이터 행 (bg-input + 좌측 화살표 hover)
 * - IGGGChip:         capsule 칩 (selected → solid)
 * - IGGGInput:        v1.1 input (bg-input → focus bg-input-focus)
 *
 * 모든 컴포넌트는 .iggg-* CSS 클래스만 사용 (iggg-ui.css 의 토큰 기반).
 * 기존 컴포넌트와 충돌하지 않도록 className 추가 패턴.
 */
import { forwardRef } from "react";

export function IGGGShell({ children, className = "", style, ...rest }) {
  return (
    <div className={`iggg-shell ${className}`} style={style} {...rest}>
      {children}
    </div>
  );
}

export function IGGGBrand({
  mark = "ESTIMATE",
  sub = "internal · estimate",
  onClick,
  className = "",
}) {
  return (
    <div
      className={`iggg-brand ${className}`}
      onClick={onClick}
      style={onClick ? { cursor: "pointer" } : undefined}
    >
      <span className="iggg-brand-mark">{mark}</span>
      <span className="iggg-brand-sub">{sub}</span>
    </div>
  );
}

export function IGGGSectionHeader({ num, title, sub, className = "" }) {
  return (
    <div className={`iggg-section-header ${className}`}>
      {num !== undefined && <span className="iggg-section-num">{num}</span>}
      <span className="iggg-section-title">{title}</span>
      {sub && <span className="iggg-section-sub">{sub}</span>}
    </div>
  );
}

export function IGGGCard({
  children,
  variant = "default",
  interactive = false,
  className = "",
  ...rest
}) {
  const cls = [
    "iggg-card",
    variant === "solid" && "iggg-card--solid",
    variant === "dashed" && "iggg-card--dashed",
    variant === "compact" && "iggg-card--compact",
    interactive && "iggg-card--interactive",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={cls} {...rest}>
      {children}
    </div>
  );
}

export function IGGGRow({ title, meta, right, onClick, className = "", as = "div", style, ...rest }) {
  const Tag = as;
  return (
    <Tag
      className={`iggg-row ${className}`}
      onClick={onClick}
      style={onClick ? { cursor: "pointer", ...style } : style}
      {...rest}
    >
      <div className="iggg-row__main">
        {title && <div className="iggg-row__title">{title}</div>}
        {meta && <div className="iggg-row__meta">{meta}</div>}
      </div>
      {right}
    </Tag>
  );
}

export function IGGGChip({ selected = false, children, onClick, className = "", ...rest }) {
  return (
    <button
      type="button"
      className={`iggg-chip ${selected ? "is-selected" : ""} ${className}`}
      aria-selected={selected}
      onClick={onClick}
      {...rest}
    >
      {children}
    </button>
  );
}

export const IGGGInput = forwardRef(function IGGGInput(
  { mono = false, className = "", ...rest },
  ref
) {
  return (
    <input
      ref={ref}
      className={`iggg-input ${mono ? "iggg-input--mono" : ""} ${className}`}
      {...rest}
    />
  );
});

export function IGGGLabel({ children, sub, htmlFor, className = "" }) {
  return (
    <label htmlFor={htmlFor} className={`iggg-label ${className}`}>
      {children}
      {sub && <span className="iggg-label__sub">{sub}</span>}
    </label>
  );
}

export function IGGGButton({
  variant = "outline",
  cap = false,
  className = "",
  children,
  ...rest
}) {
  const variantCls =
    variant === "primary"      ? "iggg-btn--primary" :
    variant === "mono-primary" ? "iggg-btn--mono-primary" :
    variant === "outline"      ? "iggg-btn--outline" :
    variant === "ghost"        ? "iggg-btn--ghost" :
    variant === "danger"       ? "iggg-btn--danger" : "";
  const cls = ["iggg-btn", variantCls, cap && "iggg-btn--cap", className]
    .filter(Boolean)
    .join(" ");
  return (
    <button type="button" className={cls} {...rest}>
      {children}
    </button>
  );
}
